import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useState, useMemo } from "react";
import { format } from "date-fns";
import { DayPicker } from "react-day-picker";
import { useLayer } from "react-laag";
import { FocusScope } from "@react-aria/focus";
import { IconArrowLeft, IconArrowRight, IconDate } from "@bynder/icons";
import { RANGE_SEPARATOR } from "./constants.js";
import { useZIndex } from "./useZIndex.js";
import { checkAndConvertDateString } from "./checkAndConvertDateString.js";
import { useKeyDown } from "./useKeyDown.js";
import { Container } from "./DatePicker.styles.js";
import { Input } from "./Input.js";
import { InteractiveIcon } from "./InteractiveIcon.js";
import { useDateFnsLocale } from "./useDateFnsLocale.js";
import { Flex } from "./Flex.js";
import { Button } from "./Button.js";
import { useTranslate } from "./useTranslate.js";
import { fixLayerProps } from "./fixLayerProps.js";
import { DateRangePickerView } from "./DateRangePickerView.js";
const LOCALISED_DATE_FORMAT = "P";
function DatePicker({
  trigger,
  onChange,
  value,
  disabledDays,
  hasClearButton = false,
  hasApplyButton = false,
  inputProps,
  selection = "single"
}) {
  const zIndex = useZIndex("datepicker");
  const locale = useDateFnsLocale();
  const {
    translate
  } = useTranslate();
  const components = {
    Button: ({
      onClick,
      role,
      "aria-label": ariaLabel = "",
      title,
      children,
      disabled,
      ...props
    }) => {
      return /* @__PURE__ */ jsx(Button, {
        "aria-label": ariaLabel,
        icon: children,
        isDisabled: disabled,
        onClick,
        variant: "clean",
        ...props
      });
    },
    Chevron: ({
      orientation
    }) => {
      return orientation === "left" ? /* @__PURE__ */ jsx(IconArrowLeft, {}) : /* @__PURE__ */ jsx(IconArrowRight, {});
    }
  };
  const [selectedDate, setSelectedDate] = useState();
  const defaultPlaceholder = useMemo(() => {
    const day = 22;
    const month = 11;
    const year = 3333;
    const placholderDate = format(new Date(year, month - 1, day), LOCALISED_DATE_FORMAT, {
      locale
    });
    const placeholderText = placholderDate.replaceAll(String(day)[0], "D").replaceAll(String(month)[0], "M").replaceAll(String(year)[0], "Y");
    return selection === "single" ? placeholderText : placeholderText + RANGE_SEPARATOR + placeholderText;
  }, [locale, selection]);
  const labels = {
    labelNext: () => translate("datepicker.next-month"),
    labelPrevious: () => translate("datepicker.previous-month")
  };
  const [isOpen, setIsOpen] = useState(false);
  const [userInput, setUserInput] = useState("");
  function singleDayFormatted(dateValue) {
    return dateValue ? format(dateValue, LOCALISED_DATE_FORMAT, {
      locale
    }) : "";
  }
  function multiDayFormatted(dateValue) {
    const {
      from,
      to
    } = {
      from: singleDayFormatted(dateValue == null ? void 0 : dateValue.from),
      to: singleDayFormatted(dateValue == null ? void 0 : dateValue.to)
    };
    return to ? from + RANGE_SEPARATOR + to : from;
  }
  function getDisplayValue(dateValue) {
    if (!dateValue) {
      return "";
    }
    return selection === "range" ? multiDayFormatted(dateValue) : singleDayFormatted(dateValue);
  }
  const displayValue = userInput || getDisplayValue(value);
  const {
    triggerProps,
    layerProps,
    renderLayer
  } = useLayer({
    isOpen,
    onParentClose: () => close(),
    auto: true,
    triggerOffset: 4,
    onOutsideClick: () => close(),
    placement: "bottom-start",
    preferX: "left",
    preferY: "bottom",
    possiblePlacements: ["bottom-start", "top-start"]
  });
  fixLayerProps(layerProps);
  useKeyDown("Escape", () => {
    if (isOpen) {
      close();
    }
  });
  function open() {
    setIsOpen(true);
    if (hasApplyButton) {
      setSelectedDate(value);
    }
  }
  function close() {
    setIsOpen(false);
  }
  function handleDaySelect(date) {
    if (!hasApplyButton && date !== void 0) {
      onChange(date);
      close();
    }
    setSelectedDate(date);
  }
  function changeDisplayValue(text) {
    if (!text) {
      clear();
    } else {
      setUserInput(text);
    }
  }
  function finishEditing() {
    const {
      isTextValid,
      date
    } = checkAndConvertDateString({
      text: displayValue,
      dateFormat: LOCALISED_DATE_FORMAT,
      locale,
      disabledDays,
      isRange: selection === "range"
    });
    const dateValue = isTextValid ? date : void 0;
    onChange(dateValue);
    setUserInput("");
  }
  function clear() {
    onChange();
    setSelectedDate(void 0);
    setUserInput("");
  }
  function applyDate() {
    onChange(selectedDate);
    close();
  }
  const internalMethods = {
    open,
    close,
    toggle: () => {
      if (isOpen) {
        close();
      } else {
        open();
      }
    },
    changeDisplayValue,
    finishEditing
  };
  function renderDefaultTrigger() {
    return /* @__PURE__ */ jsx(Input, {
      ...inputProps,
      ref: triggerProps.ref,
      value: displayValue,
      onFocus: () => close(),
      onBlur: finishEditing,
      onChange: changeDisplayValue,
      placeholder: (inputProps == null ? void 0 : inputProps.placeholder) || defaultPlaceholder,
      hasClearButton: (inputProps == null ? void 0 : inputProps.hasClearButton) || hasClearButton,
      iconRight: /* @__PURE__ */ jsx(InteractiveIcon, {
        label: translate("datepicker.open-calendar"),
        onClick: (e) => {
          e.stopPropagation();
          open();
        },
        isDisabled: inputProps == null ? void 0 : inputProps.isDisabled,
        icon: /* @__PURE__ */ jsx(IconDate, {})
      })
    });
  }
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [trigger ? trigger({
      ref: triggerProps.ref,
      placeholder: defaultPlaceholder,
      displayValue,
      isOpen
    }, internalMethods) : renderDefaultTrigger(), isOpen && renderLayer(/* @__PURE__ */ jsx(Container, {
      $zIndex: zIndex,
      ...layerProps,
      children: /* @__PURE__ */ jsxs(FocusScope, {
        contain: true,
        restoreFocus: true,
        children: [selection === "single" ? /* @__PURE__ */ jsx(DayPicker, {
          locale,
          labels,
          components,
          defaultMonth: value,
          showOutsideDays: true,
          mode: "single",
          disabled: disabledDays,
          selected: hasApplyButton ? selectedDate : value,
          onSelect: handleDaySelect,
          autoFocus: true,
          formatters: {
            formatWeekdayName: (weekday, options) => format(weekday, "cccccc", options)
          }
        }) : /* @__PURE__ */ jsx(DateRangePickerView, {
          disabledDays,
          labels,
          setRange: (date) => hasApplyButton ? setSelectedDate(date) : onChange(date),
          valueRange: hasApplyButton ? selectedDate : value,
          components
        }), hasClearButton || hasApplyButton ? /* @__PURE__ */ jsxs(Flex, {
          gap: "3",
          children: [hasClearButton && /* @__PURE__ */ jsx(Button, {
            isFullWidth: true,
            onClick: clear,
            children: translate("datepicker.clear-date")
          }), hasApplyButton && /* @__PURE__ */ jsx(Button, {
            variant: "primary",
            isDisabled: !selectedDate,
            isFullWidth: true,
            onClick: applyDate,
            children: translate("datepicker.apply-date-range")
          })]
        }) : null]
      })
    }))]
  });
}
export {
  DatePicker
};
//# sourceMappingURL=DatePicker.js.map
